// Slide-over panel triggered by the megaphone icon in the top bar.
// Fetches /CHANGELOG.md on first open, caches for the session, and
// renders a tiny markdown subset: level-2 release headers + bullet
// lists. Deliberately homegrown — the changelog's grammar is three
// element types, and pulling in a 30-40 KB markdown parser for a file
// we ourselves author isn't justified. Since we author the source, we
// don't need DOMPurify either; this renderer emits plain React nodes
// (no dangerouslySetInnerHTML anywhere).
//
// Props:
//   open:    boolean    — controls visibility
//   onClose: () => void — called on Esc, backdrop click, or × button
//   theme:   object     — theme tokens from App (S). Optional; falls
//                         back to neutral defaults.

import { useEffect, useRef, useState } from "react";
import { markSeen } from "./patchNotes";

// Parse the changelog into a list of { version, date, items } sections.
// Anything outside the grammar (level-1 title, blank lines, commentary)
// is ignored.
function parseChangelog(md) {
  if (typeof md !== "string") return [];
  const out = [];
  let cur = null;
  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trimEnd();
    const h2 = line.match(/^##\s+v(\d+\.\d+\.\d+)\s+\u2014\s+(\d{4}-\d{2}-\d{2})/);
    if (h2) {
      if (cur) out.push(cur);
      cur = { version: h2[1], date: h2[2], items: [] };
      continue;
    }
    if (!cur) continue;
    const li = line.match(/^-\s+(.+)$/);
    if (li) cur.items.push(li[1]);
    // non-header, non-bullet lines inside a section are silently
    // dropped — keeps the output a predictable version-header + list.
  }
  if (cur) out.push(cur);
  return out;
}

// Render inline-light markdown inside a bullet point: backticks for
// <code> and **bold**. Enough to make the changelog read nicely without
// dragging in a full parser.
function renderInline(text) {
  const parts = [];
  let idx = 0;
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)/g;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > idx) parts.push(text.slice(idx, m.index));
    if (m[1]) {
      parts.push(
        <code key={`k-${key++}`} style={{ fontFamily: "monospace", fontSize: "0.92em", padding: "1px 5px", borderRadius: 4, background: "rgba(127,127,127,0.15)" }}>
          {m[1].slice(1, -1)}
        </code>
      );
    } else if (m[2]) {
      parts.push(<strong key={`k-${key++}`}>{m[2].slice(2, -2)}</strong>);
    }
    idx = re.lastIndex;
  }
  if (idx < text.length) parts.push(text.slice(idx));
  return parts;
}

function Section({ section, theme: t }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h3 style={{
        fontSize: 14, fontWeight: 700, margin: "0 0 6px",
        color: t.text || "inherit",
        display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap",
      }}>
        <span>v{section.version}</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: t.text3 || "#888", fontFamily: "monospace" }}>
          {section.date}
        </span>
      </h3>
      {section.items.length > 0 && (
        <ul style={{ margin: "4px 0 0", paddingLeft: 20, lineHeight: 1.6, fontSize: 13, color: t.text2 || "inherit" }}>
          {section.items.map((item, i) => <li key={i} style={{ marginBottom: 2 }}>{renderInline(item)}</li>)}
        </ul>
      )}
    </section>
  );
}

export default function WhatsNewPanel({ open, onClose, theme }) {
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetched = useRef(false);

  // Fetch on first open. Cache for the session — users reopening the
  // panel in the same tab don't re-hit the network.
  useEffect(() => {
    if (!open || fetched.current) return;
    fetched.current = true;
    setLoading(true);
    fetch("/CHANGELOG.md", { credentials: "omit", cache: "no-store" })
      .then(r => r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(txt => { setMd(txt); setError(null); setLoading(false); })
      .catch(e => { setError(e.message || "fetch failed"); setLoading(false); });
  }, [open]);

  // Mark seen once content is loaded AND the panel is open — covers both
  // the normal path and the "retry after a failed fetch" path.
  useEffect(() => {
    if (open && md) markSeen(md);
  }, [open, md]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const t = theme || {};
  const sections = parseChangelog(md);

  return (
    <>
      {/* Backdrop — click to close */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="cf-modal-backdrop"
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)",
        }}
      />
      {/* Slide-over */}
      <aside
        role="dialog"
        aria-label="What's New"
        className="cf-slide-in-right"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: 380, maxWidth: "90vw",
          background: t.bg || "#ffffff",
          color: t.text || "#111827",
          borderLeft: `1px solid ${t.border2 || "#e5e7eb"}`,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.22)",
          zIndex: 901,
          display: "flex", flexDirection: "column",
          fontFamily: "inherit",
        }}
      >
        <header style={{
          padding: "18px 20px 14px",
          borderBottom: `1px solid ${t.border || "#eef0f3"}`,
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
        }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: t.text }}>What's New</h2>
            <div style={{ fontSize: 11, color: t.text3 || "#94a3b8", marginTop: 3 }}>
              Latest updates to ChatFort AI
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close what's new"
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: t.text2 || "#64748b", fontSize: 22, lineHeight: 1,
              padding: 4, marginTop: -2,
            }}
          >×</button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
          {loading && (
            <div style={{ color: t.text3 || "#94a3b8", fontSize: 13, fontFamily: "monospace" }}>Loading…</div>
          )}
          {error && !loading && (
            <div style={{ color: t.danger || "#dc2626", fontSize: 13 }}>
              Couldn't load changelog: {error}
            </div>
          )}
          {!loading && !error && sections.length === 0 && md && (
            <div style={{ color: t.text3 || "#94a3b8", fontSize: 13 }}>No entries yet.</div>
          )}
          {!loading && !error && sections.map(s => <Section key={s.version} section={s} theme={t} />)}
        </div>

        <footer style={{
          padding: "12px 20px", borderTop: `1px solid ${t.border || "#eef0f3"}`,
          fontSize: 12,
        }}>
          <a
            href="https://github.com/joshtk01/chatfort-pwa/blob/main/CHANGELOG.md"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: t.accent || "#3b82c4", textDecoration: "none" }}
          >
            See the full changelog on GitHub →
          </a>
        </footer>
      </aside>
    </>
  );
}
